// import AnimatedCluster from 'ol-ext/layer/AnimatedCluster';
import 'url-polyfill';
import Map from 'ol/Map';
// import { unByKey } from 'ol/Observable';
import Feature from 'ol/Feature';
import View from 'ol/View';
import Point from 'ol/geom/Point';
import Overlay from 'ol/Overlay';
import KML from 'ol/format/KML';
import WMSCapabilities from 'ol/format/WMSCapabilities';
import GeoJSON from 'ol/format/GeoJSON';
import {
    Tile as TileLayer,
    Vector as VectorLayer,
    Layer,
} from 'ol/layer';
import {
    Cluster,
    OSM,
    Vector as VectorSource,
    BingMaps,
    TileWMS,
} from 'ol/source';
import {
    createEmpty,
    extend,
} from 'ol/extent';
import {
    Circle as CircleStyle,
    Icon,
    Fill,
    Stroke,
    Style,
    Text,
} from 'ol/style';
import { transform } from 'ol/proj';
import Zoom from 'ol/control/Zoom';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import * as helpers from '../../js/helpers';

/**
 * Class represents StratisMap extension.
 */
class StratisMap {
    /**
     * Create stratis map.
     * @param {string|HTMLElement} selector - map container.
     * @param {object} options - settings object.
     * @returns {*}
     */
    constructor(selector = '[data-map]', options = {}) {
        if (typeof selector === 'string') {
            const elements = [...document.querySelectorAll(selector)];

            if (elements.length > 1) {
                return this._generateInstances(elements, options);
            }
        }

        this.element = typeof selector === 'string' ? document.querySelector(selector) : selector;

        if (!this.element) {
            console.error('Any elements');
        }

        this.id = helpers.getUniqId();

        this.element.setAttribute('data-storage-id', this.id);

        if (!window.maps) {
            window.maps = [];
        }

        this.created = false;
        // ContextContainers
        this.mapContextContainer = null;
        this.popupContextContainer = null;
        // Openlayers VectorSource
        this.source = null;
        // Get map data from url
        this.dataUrl = this.element.getAttribute('data-map-markers') || null;
        // markers Object (JSON)
        this.markers = null;
        // Array of Marker features (not includes GeoJSON features and KML features).
        this.features = [];
        // Array of layers
        this.layers = [];
        // Openlayers ClusterSource for ClusterLayer
        this.clusterSource = null;
        // Openlayers ClusterLayer
        this.clusters = null;
        // Openlayers map instance
        this.map = null;
        // Cover overlay element
        this.mapCover = null;
        // currentPageID for BEdevs
        this.pageId = this.element.getAttribute('data-page-id') || 1;
        // For manual point, we already have data and need only show popup without requesting data
        this.noRequest = this.element.getAttribute('data-no-request') || false;
        // Openlayers styles cache (need for performance)
        this.styleCache = {};
        // Popup url
        this.popupUrl = null;
        // Selected Marker
        this.selectedMarker = null;
        // Layers loading queue
        this.layersQueue = [];
        // All features Array from GeoJSON, KML etc.
        this.allFeatures = [];
        // Layer switcher
        this.layerSwitcher = null;
        // Raster layers
        this.backgroundLayers = [
            {
                type: 'OSM',
                label: 'Vue plan',
                source: {},
            },
            {
                type: 'BingMaps',
                label: 'Vue satellite',
                source: {
                    key: 'Aj6XtE1Q1rIvehmjn2Rh1LR2qvMGZ-8vPS9Hn3jCeUiToM77JFnf-kFRzyMELDol',
                    imagerySet: 'Aerial',
                },
            },
            // {
            //     type: 'WMS',
            //     label: 'Aero',
            //     url: 'https://sig.bayonne.fr/mapcache/wms?SERVICE=WMS&REQUEST=GetCapabilities',
            // },
        ];

        // Overlay states
        this.overlayActive = false;
        this.overlayClusterActive = false;

        // Map states
        this.isReady = false;

        // Custom events
        this.events = {
            popupShow: 'popupShow',
            popupClose: 'popupClose',
            mapReady: 'mapReady',
            mapBeforeUpdate: 'mapBeforeUpdate',
            mapAfterUpdate: 'mapAfterUpdate',
        };

        this.options = {
            // development mode
            devMode: {
                init: true,
                popupUrl: '/map-annuaire-popup.html', // The path will be localhost:8080/popup-annuaire.html
            },
            // Zoom on content when loaded
            zoomOnLoad: 1,
            // ZoomToExtent by clicking on feature
            zoomToExtent: 1,
            // ZoomToExtent after loading new features
            zoomToExtentAfterLoad: 1,
            // Enable/disable zooming after closing popup
            zoomOnContentAfterPopupClose: 1,
            // Enable/Disable cluster animation
            animateCluster: 1,
            // Icons
            defaultMarker: '/images/map/marker.png',
            selectedMarker: '/images/map/selected-marker.png',
            // Map center by default
            defaultLat: 48.8566,
            defaultLon: 2.3522,
            // Map zoom settings
            defaultZoom: 5,
            minZoom: 0,
            maxZoom: 18,
            // Zoom controls settings
            showZoomButtons: 1,
            zoomInTipLabel: 'Zoomer',
            zoomOutTipLabel: 'Dézoomer',
            // Map actions
            mouseWheelZoom: true,
            // Context Containers
            popupContextContainer: 'js-map-popup-context',
            mapContextContainer: 'js-map-context',
            // Some events logic
            // showOverlayOn: 'mouseover', // TODO: implement overlay show logic
            // Params for TYPO3 devs
            type: 520,
            BEController: 'tx_news_pi1[overwriteDemand][idList]',
            // Use custom OSM service
            OSMService: '', // Wikimedia example: https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}.png
            // Map settings
            padding: [50, 30, 50, 30], // Padding for view
            map: {
                classLoading: '-is-loading',
            },
            // Cover overlay settings
            cover: {
                classActive: '-is-active',
            },
            // Layer switcher selector
            layerSwitcher: {
                selector: 'js-layers-trigger',
            },
            // Popup settings
            popup: {
                classOpen: '-is-open',
                classControlsActive: '-is-active',
                classShowAction: 'js-map-show-popup',
                classCloseAction: 'js-map-close-popup',
            },
            // Overlay settings
            overlay: {
                offset: [0, 0],
                clusterOffset: [0, 40],
                className: 'popup-label',
                positioning: 'bottom-center',
                innerClassName: 'popup-label__message',
            },
            // Marker settings
            marker: {
                selector: 'js-map-marker',
            },
            // Cluster settings
            cluster: {
                openPopupByClick: false,
                color: '72, 72, 72',
                colorMedium: '', // TODO: implement cluster color change
                colorLarge: '', // TODO: implement cluster color change
                radius: 25,
                radiusMedium: 25, // TODO: implement cluster radius change
                radiusLarge: 25, // TODO: implement cluster radius change
                text: '',
                font: '16px Roboto Condensed',
                fontColor: '#fff',
                strokeWidth: 7,
            },
            // Callbacks
            beforeInit: () => {},
            afterInit: () => {},
            beforeMapClick: () => {},
            afterMapClick: () => {},
            beforeMarkerClick: () => {},
            afterMarkerClick: () => {},
            beforeDataFetch: () => {},
            afterDataFetch: () => {},
            beforeDestroy: () => {},
            afterDestroy: () => {},
            beforeUpdate: () => {},
            afterUpdate: () => {},
        };

        // Extend object options with client options
        if (typeof options === 'object') {
            this.options = this._extendDefaults(this.options, options);
        }

        this._getDataOptions();

        console.log(`DEV Mode Init: ${this.options.devMode.init}`);

        // TODO: Switch devMode to true
        if (this.options.devMode.init) {
            this.popupUrl = this.options.devMode.popupUrl;
        } else {
            this.popupUrl = window.location.origin;
        }

        // Bindings
        this._addStyles = this._addStyles.bind(this);
        this._markerHandler = this._markerHandler.bind(this);
        this._overlayHandler = this._overlayHandler.bind(this);
        this._popupHandler = this._popupHandler.bind(this);
        this._resizeHandler = this._resizeHandler.bind(this);
        this._backgroundLayersHandler = this._backgroundLayersHandler.bind(this);

        // Start to initialize map
        this.init();
    }

    /**
     * Merge N objects to one.
     * @param {Objects} objects - list of objects.
     * @returns {Object} - merged object.
     * @private
     */
    _extendDefaults(...objects) {
        const isObject = obj => obj && typeof obj === 'object';

        return objects.reduce((prev, obj) => {
            Object.keys(obj)
                .forEach((key) => {
                    const previousValue = prev[key];
                    const objectValue = obj[key];
                    if (Array.isArray(previousValue) && Array.isArray(objectValue)) {
                        prev[key] = objectValue;
                    } else if (isObject(previousValue) && isObject(objectValue)) {
                        prev[key] = this._extendDefaults(previousValue, objectValue);
                    } else {
                        prev[key] = objectValue;
                    }
                });

            return prev;
        }, {});
    }

    /**
     * Extend options from HTML data attribute.
     * @private
     */
    _getDataOptions() {
        const options = this.element.getAttribute('data-map-options');

        if (options) {
            try {
                this.options = this._extendDefaults(this.options, JSON.parse(options));
                this._normalizeOptions();
            } catch (e) {
                console.error('Incorrect options in data-map-options');
            }
        }
    }

    /**
     * Normalize options types.
     * @private
     */
    _normalizeOptions() {
        this.options = this._extendDefaults(this.options, {
            defaultLat: +this.options.defaultLat,
            defaultLon: +this.options.defaultLon,
            defaultZoom: +this.options.defaultZoom,
            minZoom: +this.options.minZoom,
            maxZoom: +this.options.maxZoom,
            zoomOnLoad: +this.options.zoomOnLoad,
            zoomToExtent: +this.options.zoomToExtent,
            zoomToExtentAfterLoad: +this.options.zoomToExtentAfterLoad,
            zoomOnContentAfterPopupClose: +this.options.zoomOnContentAfterPopupClose,
            animateCluster: +this.options.animateCluster,
            showZoomButtons: +this.options.showZoomButtons,
            mouseWheelZoom: +this.options.mouseWheelZoom,
        });
    }

    /**
     * Generete instances if we have more than one instance of StratisMap.
     * @param {HTMLElement[]} elements - array of DOM elements.
     * @param {Object} config - settings for StratisMap instance.
     * @returns {StratisMap[]} - Array of StratisMap instances.
     * @private
     */
    _generateInstances(elements, config) {
        return elements.reduce((instances, element) => {
            instances.push(new StratisMap(element, config));
            return instances;
        }, []);
    }

    /**
     * Add styles for Cluster
     * @param color {string} - cluster color
     * @param radius {number} - cluster radius
     * @param text {string} - cluster text
     * @returns {Style[]} - array of ol.Style objects
     * @private
     */
    _addClusterStyle(color = this.options.cluster.color, radius = this.options.cluster.radius, text = this.options.cluster.text) {
        return [
            new Style({
                image: new CircleStyle({
                    radius: radius + 3,
                    stroke: new Stroke({
                        color: `rgba(${color}, 0.3)`,
                        width: this.options.cluster.strokeWidth,
                    }),
                }),
            }),
            new Style({
                image: new CircleStyle({
                    fill: new Fill({
                        color: `rgba(${color}, 0.6)`,
                    }),
                    radius,
                }),
                text: new Text({
                    text,
                    font: this.options.cluster.font,
                    fill: new Fill({
                        color: this.options.cluster.fontColor,
                    }),
                }),
            }),
        ];
    }

    /**
     * Add styles for Single Marker
     * @param marker {Object} - feature marker
     * @returns {Style} - ol.Style object
     * @private
     */
    _addSingleMarkerStyle(marker, props = {}) {
        let styleProp;
        let src;

        if (props.icon) {
            if (props.icon.indexOf('.svg') >= 0 && helpers.getBrowserName() === 'Internet Explorer') {
                styleProp = props.selected ? 'selectedMarker' : 'defaultMarker';
                src = this.options[styleProp];
            } else {
                if (props.iconSelected) {
                    styleProp = props.selected ? props.iconSelected : props.icon;
                } else {
                    styleProp = props.selected ? this.options.selectedMarker : props.icon;
                }

                src = styleProp;
            }
        } else {
            styleProp = props.selected ? 'selectedMarker' : 'defaultMarker';
            src = this.options[styleProp];
        }

        return new Style({
            image: new Icon({
                anchor: [0.5, 1],
                crossOrigin: 'anonymous',
                src,
            }),
        });
    }

    /**
     * Style function when loading features.
     * @param feature {Feature} - current feature instance
     * @param resolution {number} - ol resolution
     * @returns {*} - style
     */
    _addStyles(feature, resolution) {
        const size = feature.get('features') ? feature.get('features').length : 1;
        const markers = [];
        let style = this.styleCache[size];

        // Style only visible markers
        feature.get('features').forEach(marker => (!marker.get('hidden') ? markers.push(marker) : false));

        if (markers.length > 1) {
            if (!style) {
                // const color = size > 25
                //     ? '248, 128, 0'
                //     : size > 8
                //         ? '248, 192, 0'
                //         : '128, 192, 64';
                // const radius = Math.max(20, Math.min(size * 0.75, 30));
                const color = this.options.cluster.color;
                const radius = this.options.cluster.radius;
                const text = this.options.cluster.text || size.toString();

                style = this._addClusterStyle(color, radius, text);
                this.styleCache[size] = style;
            }

            return style;
        }

        // Style single marker
        if (markers.length === 1) {
            const markerProps = {
                icon: markers[0].get('icon'),
                iconSelected: markers[0].get('iconSelected'),
                selected: markers[0].get('selected'),
                uid: +markers[0].get('uid'),
            };

            let styleProp;

            if (markerProps.icon) {
                if (markerProps.iconSelected) {
                    styleProp = markerProps.selected ? `marker-${markerProps.uid}-selected` : `marker-${markerProps.uid}`;
                } else {
                    styleProp = markerProps.selected ? 'selectedMarker' : `marker-${markerProps.uid}`;
                }
            } else {
                styleProp = markerProps.selected ? 'selectedMarker' : 'defaultMarker';
            }

            style = this.styleCache[styleProp];

            if (!style) {
                style = this._addSingleMarkerStyle(markers[0], markerProps);
                this.styleCache[styleProp] = style;
            }

            return style;
        }
    }

    /**
     * Render LayerSwitcher options.
     * @private
     */
    _renderSelectboxOptions() {
        this.layerSwitcher = this.mapContextContainer.querySelector(`.${this.options.layerSwitcher.selector}`);

        if (this.layerSwitcher) {
            this.backgroundLayers.forEach((layer, index) => {
                const option = document.createElement('option');

                option.innerText = layer.label;
                option.value = index;

                this.layerSwitcher.appendChild(option);
            });
        }
    }

    /**
     * Set visibility to choosen layer in LayerSwitcher
     * @param {Nubmer} selectedIndex - Choosed layer index.
     * @private
     */
    _setBackgroundLayer(selectedIndex) {
        this.backgroundLayers.forEach((layer, index) => {
            layer.realLayer.setVisible(selectedIndex === index);
        });
    }

    /**
     * Layers switcher handler.
     * @param {Event} event - browser event.
     * @private
     */
    _backgroundLayersHandler(event) {
        this._setBackgroundLayer(+event.target.value);
    }

    /**
     * TODO: Get preview dynamic
     * Return the tile image of the source.
     * @param {ol.Coordinate|undefined} lonlat The center of the preview.
     * @param {number} resolution of the preview.
     * @return {String} the preview url
     */
    _getLayerPreview(lonlat = [21020, 6355964], resolution = 150) {
        // var coord = this.getTileGrid().getTileCoordForCoordAndResolution(lonlat, resolution);
        // var fn = this.getTileUrlFunction();
        // return fn.call(this, coord, this.getProjection());
    }

    /**
     * Create Vector source for markers.
     * @private
     */
    _createSource() {
        this.source = new VectorSource({
            features: this.features,
        });
    }

    /**
     * Create map OSM layer.
     * @private
     */
    _createRasterLayers() {
        const parser = new WMSCapabilities();

        this.backgroundLayers.forEach((layer, index) => {
            let source;

            switch (layer.type) {
                case 'OSM':
                    if (this.options.OSMService) {
                        layer.source.url = this.options.OSMService;
                    }

                    source = new OSM(layer.source);
                    break;
                case 'BingMaps':
                    source = new BingMaps(layer.source);
                    break;
                case 'WMS':
                    // fetch(layer.url)
                    //     .then((res) => {
                    //         console.log(res, res.text());
                    //         return res.text();
                    //     })
                    //     .then((text) => {
                    //         const result = parser.read(text);
                    //         console.log(text, result);
                    //         // const layers = result.Capability.Layer.Layer;
                    //         // console.log(layers);
                    //     })
                    //     .catch(err => console.error(err));
                    break;
                default:
                    source = new OSM();
            }

            const raster = new TileLayer({
                source,
            });

            if (index > 0) {
                raster.setVisible(false);
            }

            layer.realLayer = raster;

            this.layers.push(raster);
        });

        this._renderSelectboxOptions(this.backgroundLayers);
    }

    /**
     * Create Cluster layer for markers.
     * @private
     */
    _createCluster() {
        if (this.source) {
            this.clusterSource = new Cluster({
                distance: 50,
                source: this.source,
            });

            const clusterSettings = {
                name: 'cluster',
                source: this.clusterSource,
                style: this._addStyles,
            };

            // AnimateCluster if enabled
            // if (this.options.animateCluster) {
            //     this.clusters = new AnimatedCluster(clusterSettings);
            // } else {
            //     this.clusters = new VectorLayer(clusterSettings);
            // }

            this.clusters = new VectorLayer(clusterSettings);

            this.layers.push(this.clusters);
        }
    }

    /**
     * Create layer according to params.
     * @param {Object} layer - object with layer settings: uid, type, url, features ....
     * @private
     */
    _createLayer(layer) {
        const uid = +layer.uid;
        let source;

        if (!uid || !layer.type) {
            return;
        }

        switch (layer.type) {
            case 'kml':
                source = new VectorSource({
                    url: layer.url,
                    format: new KML(),
                });
                break;
            case 'geojson':
                if (layer.url) {
                    source = new VectorSource({
                        format: new GeoJSON(),
                        url: layer.url,
                    });
                } else {
                    source = new VectorSource({
                        features: (new GeoJSON()).readFeatures(layer.features),
                    });
                }

                break;
            default:
                source = null;
        }

        const mapLayer = new VectorLayer({
            source,
        });

        mapLayer.set('uid', uid);

        this.layersQueue.push(uid);
        this.layers.push(mapLayer);
    }

    /**
     * Get layers from HTML.
     * @returns {HTMLElement[]} Array of layers which was passed to HTML.
     * @private
     */
    _getLayersFromHTML() {
        return [...this.mapContextContainer.querySelectorAll('[data-layer]')];
    }

    /**
     * Create all passed layers (KML, GeoJSON)
     * @private
     */
    _createLayers() {
        this.layers.forEach((layer) => {
            const uid = +layer.get('uid');

            if (uid) {
                this.layersQueue.push(uid);
            }
        });
        // // const jsonLayers
        // const htmlLayers = this._getLayersFromHTML();
        //
        // if (htmlLayers.length) {
        //     htmlLayers.forEach((layer) => {
        //         const layerType = layer.getAttribute('data-layer-type');
        //         const layerUrl = layer.getAttribute('data-layer');
        //         const uid = +layer.getAttribute('data-uid');
        //
        //         this._createLayer({
        //             type: layerType,
        //             url: layerUrl,
        //             uid,
        //         });
        //     });
        // }
    }

    _createZoomControl() {
        this.map.getControls().forEach((control) => {
            if (control instanceof Zoom) {
                this.map.removeControl(control);

                if (this.options.showZoomButtons) {
                    const zoom = new Zoom({
                        zoomInTipLabel: this.options.zoomInTipLabel,
                        zoomOutTipLabel: this.options.zoomOutTipLabel,
                    });

                    this.map.addControl(zoom);
                }
            }
        });
    }

    _removeMouseWheelInteraction() {
        this.map.getInteractions().forEach((interaction) => {
            if (interaction instanceof MouseWheelZoom) {
                this.map.removeInteraction(interaction);
            }
        });
    }

    /**
     * Popup handler.
     * @param {Event} event
     * @private
     */
    _popupHandler(event) {
        // event.preventDefault();
        const target = event.target;

        if (this.popupControls.length) {
            helpers.removeClass(this.popupControls, this.options.popup.classControlsActive);
        }

        if (helpers.hasClass(target, this.options.popup.classCloseAction)) {
            this.closePopup();
            return false;
        }

        if (helpers.hasClass(target, this.options.popup.classShowAction)) {
            const uid = +target.getAttribute('data-uid');
            let type = this.options.type;

            if (target.getAttribute('data-type')) {
                type = +target.getAttribute('data-type');
            }

            if (!uid) {
                throw new Error(`Can't get uid. Clicked target data-uid is ${uid}`);
            }

            helpers.addClass(target, this.options.popup.classControlsActive);

            this.showPopup(uid, type);
            return false;
        }
    }

    /**
     * Marker handler.
     * @param {Event} event.
     * @private
     */
    _markerHandler(event) {
        this.options.beforeMapClick();
        const targetFeatures = this.map.getFeaturesAtPixel(event.pixel);
        const targetElement = event.originalEvent.target;
        let features = [];

        // Deselect marker if click on other.
        if (targetFeatures && this.selectedMarker) {
            this.selectedMarker.set('selected', false);
        }

        if (targetFeatures && targetFeatures.length) {
            targetFeatures.forEach((feature) => {
                this.options.beforeMarkerClick();
                const markers = feature.get('features');

                if (markers) {
                    const type = markers[0].get('type');
                    const zoomLevel = this.map.getView().getZoom();
                    let uids;

                    features = [...features, ...markers];

                    if (markers.length === 1) {
                        uids = markers[0].get('uid');
                        if (uids) {
                            this.showPopup(uids, type, false);
                        }
                        this.zoomToExtent(features);
                    } else {
                        uids = markers.map(marker => +marker.get('uid'));

                        if (zoomLevel !== this.options.maxZoom) {
                            this.zoomToExtent(features, 200, () => {
                                console.log('zoom to extent');
                                this.showOverlay(event, targetFeatures);
                            });
                        } else {
                            if (this.options.cluster.openPopupByClick) {
                                if (uids) {
                                    this.showPopup(uids, type, false);
                                }
                            }
                        }
                    }
                }

                this.options.afterMarkerClick();
            });
        } else {
            // Hide overlay if click outside of overlay or on other marker
            if (!helpers.hasClass(targetElement, this.options.overlay.className) || !helpers.findAncestor(targetElement, this.options.overlay.className)) {
                this.overlayClusterActive = false;
                this.hideOverlay();
            }
        }

        this.options.afterMapClick();
    }

    /**
     * Create overlay element which shows on click/hover on feature.
     * @param overlayParams
     * @returns {Overlay}
     */
    _createOverlay(overlayParams = {}) {
        if (!overlayParams.element) {
            const overlay = document.createElement('div');
            overlay.className = 'popup-label';
            overlay.innerHTML = '<div class="popup-label__message"></div>';
            overlay.style.cssText = 'display: block;';

            overlayParams.element = overlay;
        }

        const params = this._extendDefaults({
            positioning: 'bottom-center',
            stopEvent: false,
        }, overlayParams);

        return new Overlay(params);
    }

    /**
     * Marker overlay handler.
     * @param {Event} event
     */
    _overlayHandler(event) {
        const targetFeatures = this.map.getFeaturesAtPixel(event.pixel);

        if (targetFeatures) {
            this.showOverlay(event, targetFeatures);
        } else {
            this.hideOverlay();
        }
    }

    /**
     * Callback when layer is fully loaded.
     * @param layer
     * @private
     */
    _onLayerCreated(layer) {
        const index = this.layersQueue.indexOf(+layer.get('uid'));

        if (index > -1) {
            this.layersQueue.splice(index, 1);
        }

        if (this.layersQueue.length === 0 && !this.isReady) {
            this.isReady = true;

            if (this.options.zoomOnLoad) {
                this.zoomToExtent(this.allFeatures, 500);
            }

            // Fire mapReady event
            this.mapContextContainer.dispatchEvent(this.events.mapReady);
        }
    }

    /**
     * Callback if map is ready and all layers is loaded.
     * @private
     */
    _onMapReady() {
        if (!this.isReady) {
            this.layers.forEach((layer) => {
                const source = layer.getSource();

                if (source) {
                    //
                    // if (source instanceof VectorSource && source.getFormat() instanceof GeoJSON) {
                    //     if (source.getState() === 'ready') {
                    //         this.allFeatures.push(...source.getFeatures());
                    //         this._onLayerCreated(layer);
                    //     }
                    // }
                    source.on('change', (e) => {
                        if (source.getState() === 'ready') {
                            if (source instanceof Cluster) {
                                this.allFeatures.push(...this.clusterSource.getSource().getFeatures());
                            }

                            if (source instanceof VectorSource) {
                                this.allFeatures.push(...source.getFeatures());
                            }

                            this._onLayerCreated(layer);
                        }
                    });
                }
            });
        }
    }

    /**
     * Update map when window was resized.
     * @private
     */
    _resizeHandler() {
        this.updateSize();
    }

    /**
     * Add event listeners.
     * @private
     */
    _addListeners() {
        this.map.addEventListener('click', this._markerHandler);
        this.map.addEventListener('pointermove', this._overlayHandler);

        this.map.addEventListener('pointermove', (e) => {
            const targetFeatures = this.map.getFeaturesAtPixel(e.pixel);
            this.map.getTarget().style.cursor = targetFeatures ? 'pointer' : '';
        });

        if (this.layerSwitcher) {
            this.layerSwitcher.addEventListener('change', this._backgroundLayersHandler);
        }

        this.mapContextContainer.addEventListener('click', this._popupHandler);
        window.addEventListener('resize', this._resizeHandler);
    }

    /**
     * Remove event listeners.
     * @private
     */
    _removeListeners() {
        this.map.removeEventListener('click', this._markerHandler);
        this.map.removeEventListener('pointermove', this._overlayHandler);

        if (this.layerSwitcher) {
            this.layerSwitcher.removeEventListener('change', this._backgroundLayersHandler);
        }

        this.mapContextContainer.removeEventListener('click', this._popupHandler);
        window.removeEventListener('resize', this._resizeHandler);
    }

    /**
     * Encode an object as url query string parameters
     * - includes the leading "?" prefix
     * @param {Object} params - params object which be parsed.
     * @example input — {key: "value", alpha: "beta"}
     * @example output — output "?key=value&alpha=beta"
     * @returns empty string when given an empty object
     * @private
     */
    _encodeQueryString(params) {
        const keys = Object.keys(params);
        return keys.length
            ? `?${keys.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`).join('&')}`
            : '';
    }

    /**
     * Request data from server.
     * @param {String} url - request url.
     * @param {Object} params - query params to request.
     * @returns {Promise<string | never>}
     * @private
     */
    _requestData(url, params) {
        return fetch(url + this._encodeQueryString(params)).then(res => res.text());
    }

    /**
     * Load data for map
     * @private
     */
    _getData() {
        this.options.beforeDataFetch();

        // Load data if passed dataUrl
        if (this.dataUrl) {
            return fetch(this.dataUrl)
                .then((res) => {
                    if (res.status >= 200 && res.status < 300) {
                        return res.text();
                    }
                })
                .then(res => JSON.parse(res))
                .catch((err) => {
                    throw new Error(err.message);
                });
        }

        return new Promise((resolve) => {
            resolve({});
        });
    }

    /**
     * IE polyfill for CustomEvents.
     * @private
     */
    _customEventIEPolyfill() {
        try {
            const event = new CustomEvent('IE has CustomEvent, but doesn\'t support constructor');
        } catch (e) {
            window.CustomEvent = (
                event,
                params = {
                    bubbles: false,
                    cancelable: false,
                    detail: undefined,
                },
            ) => {
                const evt = document.createEvent('CustomEvent');
                evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
                return evt;
            };

            CustomEvent.prototype = Object.create(window.Event.prototype);
        }
    }

    /**
     * Generate custom events for map.
     * @private
     */
    _createCustomEvents() {
        this._customEventIEPolyfill();

        for (const event in this.events) {
            if (event) {
                this.events[event] = new CustomEvent(this.events[event], {
                    bubbles: true,
                    cancelable: false,
                    params: {},
                });
            }
        }
    }

    /**
     * Create overlay for map popup.
     * @return {HTMLElement} - popup element.
     */
    _createCoverOverlay() {
        this.mapCover = document.createElement('div');
        helpers.addClass(this.mapCover, 'js-map-cover');
        helpers.addClass(this.mapCover, this.options.popup.classCloseAction);
        return this.mapCover;
    }

    /**
     * Create Openlayers map.
     * @private
     */
    _createMap() {
        this.mapContextContainer = helpers.findAncestor(this.element, this.options.mapContextContainer);

        if (!this.mapContextContainer) {
            throw new Error(`Can't find node with class ${this.options.mapContextContainer}, it's important, please add it to set instance scope`);
        }

        this.mapContextContainer.appendChild(this._createCoverOverlay());

        this.popupContextContainer = this.mapContextContainer.querySelector(`.${this.options.popupContextContainer}`);
        this.popupControls = [...this.mapContextContainer.querySelectorAll('.js-map-show-popup')];

        this._getData().then((data) => {
            this._createRasterLayers();

            //
            // if (data) {
            //     TODO: updating layers from JSON
            //     if (data.layers) {
            //         // this.layers = [...this.layers, ...StratisMap.GeoJSONtoLayers(data.layers)];
            //     }
            //
            //     if (data.markers) {
            //         this.features = StratisMap.JSONtoFeatures(data.markers);
            //     }
            // }

            const HTMLMarkers = [...this.mapContextContainer.querySelectorAll(`.${this.options.marker.selector}`)];

            if (HTMLMarkers.length) {
                this.features = [...this.features, ...StratisMap.HTMLtoFeatures(HTMLMarkers)];
            }

            this.layers = [...this.layers, ...StratisMap.HTMLtoLayers(this._getLayersFromHTML())];

            this.options.afterDataFetch(this.features, this.layers);

            this._createSource();
            this._createCluster();
            this._createLayers();

            const mapWrapper = this.element.querySelector('.map__wrapper');

            this.map = new Map({
                layers: this.layers,
                target: mapWrapper,
                loadTilesWhileAnimating: true,
                view: new View({
                    center: transform([this.options.defaultLon, this.options.defaultLat], 'EPSG:4326', 'EPSG:3857'),
                    zoom: this.options.defaultZoom,
                    maxZoom: this.options.maxZoom,
                }),
            });

            this._createZoomControl();

            if (!this.options.mouseWheelZoom) {
                this._removeMouseWheelInteraction();
            }

            this.popover = this._createOverlay({
                positioning: this.options.overlay.positioning,
            });

            this.map.addOverlay(this.popover);

            // Zoom to features on load
            if (this.options.zoomOnLoad) {
                // Open popup if we have only 1 marker
                if (this.features.length === 1) {
                    const uid = +this.features[0].get('uid');
                    const type = +this.features[0].get('type');

                    if (uid) {
                        this.showPopup(uid, type);
                    }
                } else {
                    this._onMapReady();
                }
            }

            // Add listeners
            this._addListeners();
        });
    }

    /**
     * Initialize StratisMap instance
     */
    init() {
        if (this.created) {
            return;
        }

        this.options.beforeInit();
        this._createCustomEvents();
        this._createMap();
        this.created = true;
        window.maps.push(this);
        this.options.afterInit();
    }

    /**
     * TODO: Destroy StratisMap instance
     */
    destroy() {
        if (!this.created) {
            return;
        }

        this.options.beforeDestroy(this);
        this.created = false;
        this.options.afterDestroy(this);
    }

    /**
     * Find feature by uid in features.
     * @param {Number} uid - feature uid.
     * @returns {Array[]} - return Array of features.
     */
    findFeature(uid) {
        return this.features.filter(feature => feature.get('uid') === uid);
    }

    /**
     * Shows popup
     * @param {Array[]|Number} uids - popupId to get or Array of ids.
     * @param {Number} type - query type for loading popup.
     * @param {Boolean} zoomToExtent - enable/disable zooming of feature.
     */
    showPopup(uids, type = 520, zoomToExtent = true) {
        // find feature by uids
        const feature = !Array.isArray(uids) ? this.findFeature(uids) : this.findFeature(uids[0]);

        // Append current uids into custom event
        this.events.popupShow.params = { uids };

        if (!this.noRequest || this.noRequest === 'false') {
            // Add loader before request
            helpers.addClass(this.mapContextContainer, this.options.map.classLoading);

            const queryParams = {
                id: this.pageId,
                type,
            };

            queryParams[this.options.BEController] = !Array.isArray(uids) ? uids : uids.join(',');

            this._requestData(this.popupUrl, queryParams)
                .then((res) => {
                    const html = helpers.parseToHtml(res);

                    // Remove loader before pasting data.
                    helpers.removeClass(this.mapContextContainer, this.options.map.classLoading);
                    // helpers.addClass(this.mapCover, this.options.cover.classActive);

                    if (this.popupContextContainer) {
                        this.popupContextContainer.innerHTML = html.innerHTML;
                        helpers.addClass(this.popupContextContainer, '-is-open');

                        if (!Array.isArray(uids)) {
                            this.selectMarker(uids);
                        }

                        if (zoomToExtent) {
                            this.zoomToExtent(feature, 500);
                        }

                        this.updateSize();
                        // Fire popupShow event.
                        this.mapContextContainer.dispatchEvent(this.events.popupShow);
                    } else {
                        console.error(`Can't find node with class ${this.options.popupContextContainer} to paste data`);
                    }
                });
        } else {
            if (this.popupContextContainer) {
                if (!Array.isArray(uids)) {
                    this.selectMarker(uids);
                }

                // helpers.addClass(this.popupContextContainer, '-is-open');

                if (zoomToExtent) {
                    this.zoomToExtent(feature, 500);
                }

                this.updateSize();
                // Fire popupShow event.
                this.mapContextContainer.dispatchEvent(this.events.popupShow);
            }
        }
    }

    /**
     * Close popup.
     */
    closePopup() {
        helpers.removeClass(this.popupContextContainer, this.options.popup.classOpen);
        helpers.removeClass(this.mapCover, this.options.cover.classActive);

        this.resetMarkers();
        this.updateSize();
        // Fire popupClose event.
        this.mapContextContainer.dispatchEvent(this.events.popupClose);
    }

    /**
     * Deselect all markers.
     */
    resetMarkers() {
        this.features.forEach(feature => feature.set('selected', false));

        if (this.options.zoomOnContentAfterPopupClose) {
            this.zoomOnContent();
        }
    }

    /**
     * Select marker by uid.
     * @param {Number} uid - marker uid.
     */
    selectMarker(uid) {
        const feature = this.findFeature(uid);

        if (feature.length) {
            feature[0].set('selected', true);
            this.selectedMarker = feature[0];
        }
    }

    /**
     * Show map overlay (marker overlay).
     * @param {Feature[]} features - Array of map features.
     */
    showOverlay(event, features) {
        if (features) {
            const markers = features[0].get('features');
            const popoverContainer = this.popover.getElement().children[0];
            let coords = features[0].getGeometry().getCoordinates();

            if (markers) {
                popoverContainer.innerHTML = StratisMap.createMessage(markers[0]);

                if (markers.length > 1) {
                    let message = '';

                    markers.forEach((marker) => {
                        message += StratisMap.createMessage(marker);
                    });

                    popoverContainer.innerHTML = message;

                    this.popover.setOffset(this.options.overlay.clusterOffset);
                    this.overlayClusterActive = this.options.maxZoom - this.map.getView().getZoom() <= 1;
                } else {
                    this.popover.setOffset(this.options.overlay.offset);
                    this.overlayClusterActive = false;
                }
            } else {
                let message = '';

                const points = features.filter(feature => feature.getGeometry().getType() === 'Point');
                const areas = features.filter(feature => feature.getGeometry().getType() !== 'Point');

                if (points.length) {
                    points.forEach((point) => {
                        message = StratisMap.createMessage(point);
                    });
                } else {
                    areas.forEach((area) => {
                        coords = event.coordinate;
                        message = StratisMap.createMessage(area);
                    });
                }

                popoverContainer.innerHTML = message;
            }

            this.popover.setPosition(coords);
            this.overlayActive = true;
        }
    }

    /**
     * Hide map overlay (marker overlay).
     */
    hideOverlay() {
        if (this.overlayActive && !this.overlayClusterActive) {
            this.popover.setOffset([0, 0]);
            this.popover.setPosition(undefined);
            this.overlayActive = false;
            this.overlayClusterActive = false;
        }
    }

    /**
     * Update features and layers on map.
     * @param {Feature[]|null} features - array of new ol features.
     * @param {Layer[]} layers - array of new ol layers.
     */
    update(features, layers = [], callback = () => {}) {
        // Fire event mapBeforeUpdate
        this.mapContextContainer.dispatchEvent(this.events.mapBeforeUpdate);
        this.allFeatures = [];
        this.source.clear();

        if (features.length) {
            this.features = features;
            this.source.addFeatures(features);
            this.source.refresh();
        }

        this.layers.forEach((renderedLayer) => {
            if (!(renderedLayer instanceof TileLayer) && !(renderedLayer.getSource() instanceof Cluster)) {
                this.map.removeLayer(renderedLayer);
            }
        });

        if (layers.length) {
            this.isReady = false;


            layers.forEach((layer) => {
                this.layers.push(layer);
                this.layersQueue.push(+layer.get('uid'));
                this.map.addLayer(layer);
            });

            this._onMapReady();
            // Fire event mapAfterUpdate
            callback();
            this.mapContextContainer.dispatchEvent(this.events.mapAfterUpdate);
        } else {
            this.zoomOnContent(1000);
            // Fire event mapAfterUpdate
            callback();
            this.mapContextContainer.dispatchEvent(this.events.mapAfterUpdate);
        }
    }

    /**
     * Recalculate map dimentions.
     */
    updateSize() {
        this.map.updateSize();
    }

    /**
     * Update map fit and set padding to it if it needs
     * @param {Array} padding - padding for ol.View
     * @param {Boolean} zoomToExtent - zoom on all features or not
     */
    updateBounds(padding = this.options.padding, zoomToExtent = false) {
        this.set('padding', padding);

        if (zoomToExtent) {
            this.zoomOnContent();
        }
    }

    /**
     * Zoom on all loaded features and layers.
     * @param {Number} duration - animation duration.
     */
    zoomOnContent(duration = 500) {
        if (!this.allFeatures.length) {
            this.map.getLayers().forEach((layer) => {
                const source = layer.getSource();

                if (layer.get('area')) {
                    this.allFeatures.push(layer);
                }

                if (source instanceof VectorSource && !(source instanceof Cluster)) {
                    this.allFeatures.push(...source.getFeatures());
                }

                if (source instanceof Cluster) {
                    this.allFeatures.push(...this.clusterSource.getSource().getFeatures());
                }
            });
        }

        this.zoomToExtent(this.allFeatures, duration);
    }

    /**
     * Zoom to passed features.
     * @param {Feature[]} features - array of features.
     * @param {Number} duration - animation duration.
     */
    zoomToExtent(features, duration = 200, cb) {
        const featuresArray = [...features];
        const view = this.map.getView();

        const extent = featuresArray.reduce((acc, feature) => {
            if (feature instanceof Layer) {
                return extend(acc, feature.getSource().getExtent());
            }

            if (feature instanceof Feature) {
                return extend(acc, feature.getGeometry().getExtent());
            }

            return acc;
        }, createEmpty());

        if (extent.indexOf(Infinity) === -1) {
            view.fit(extent, {
                size: this.map.getSize(),
                constrainResolution: false,
                maxZoom: this.options.maxZoom,
                padding: this.options.padding,
                callback: cb,
                duration,
            });
        }
    }

    /**
     * Set value to property. Don't work with nested settings.
     * @param {String} prop - property name
     * @param {any} value - can be any value but set correct value to each property because you may have errors.
     */
    set(prop, value) {
        this.options[prop] = value;
    }

    /**
     * Add event listener for map.
     * @param {String} eventType - type of event for listen.
     * @param {Function} listener - listener function to add.
     */
    addListener(eventType, listener = () => {}) {
        this.mapContextContainer.addEventListener(eventType, listener);
    }

    /**
     * Remove event listener from map.
     * @param {String} eventType - type of event for listen.
     * @param {Function} listener - listener function to remove.
     */
    removeListener(eventType, listener = () => {}) {
        this.mapContextContainer.removeListener(eventType, listener);
    }

    /**
     * TODO: add API method for adding background layers dynamic
     */
    // addBackgroundLayer(layer) {
    //     this.backgroundLayers.push(layer);
    //     this._renderSelectboxOptions(this.backgroundLayers);
    // }

    /**
     * Transform JSON data to Features array.
     * @param {JSON|Object} json - Markers JSON or Object with markers.
     * @returns {Features[]} - Array of transformed Features.
     * @constructor
     */
    static JSONtoFeatures(json) {
        const features = [];

        for (const marker in json) {
            if (marker) {
                const feature = new Feature(new Point(transform([
                    +json[marker].lon,
                    +json[marker].lat,
                ], 'EPSG:4326', 'EPSG:3857')));

                // set default state
                feature.set('selected', false);
                feature.set('marker', true);
                // set message for marker
                feature.set('message', json[marker].message);
                // set uid for marker
                feature.set('uid', +json[marker].uid);
                // set custom icon for marker
                feature.set('icon', json[marker].icon);
                // set query type for marker (it)
                if (typeof json[marker].type !== 'undefined') {
                    feature.set('type', json[marker].type);
                }

                features.push(feature);
            }
        }

        return features;
    }

    /**
     * Transform HTML markers data to Features array.
     * @param {HTMLElement[]} html - Array of HTML elements.
     * @returns {Features[]} - Array of transformed Features.
     * @constructor
     */
    static HTMLtoFeatures(html) {
        const features = [];

        if (html.length) {
            html.forEach((marker) => {
                const {
                    lon, lat, uid, type, icon, iconSelected,
                } = marker.dataset;
                const message = marker.innerHTML;

                const feature = new Feature(new Point(transform([+lon, +lat], 'EPSG:4326', 'EPSG:3857')));

                feature.set('selected', false);
                feature.set('marker', true);
                feature.set('uid', +uid);

                if (icon) {
                    feature.set('icon', icon);
                }

                if (iconSelected) {
                    feature.set('iconSelected', iconSelected);
                }

                if (message) {
                    feature.set('message', message);
                }

                if (type) {
                    feature.set('type', +type);
                }

                features.push(feature);
            });
        }

        return features;
    }

    /**
     * Generate random features
     * @param count {number} - random features count.
     * @returns {Array} - features array.
     */
    static generateRandomFeatures(count = 200) {
        const features = new Array(count);
        const e = 4500000;

        for (let i = 0; i < count; ++i) {
            const coordinates = [2 * e * Math.random() - e, 2 * e * Math.random() - e];
            features[i] = new Feature(new Point(coordinates));
            features[i].set('selected', false);
            features[i].set('uid', i);
            features[i].set('message', `Marker ${i}`);
        }

        return features;
    }

    /**
     * Transform HTML Elements to ol.Layers.
     * @param [HTMLElements[]] html - Array of HTML Elements.
     * @returns {Layer[]} - array of ol Layers.
     * @constructor
     */
    static HTMLtoLayers(html) {
        const layers = [];

        if (html.length) {
            html.forEach((layer) => {
                const layerType = layer.getAttribute('data-layer-type');
                const layerUrl = layer.getAttribute('data-layer');
                const uid = +layer.getAttribute('data-uid');
                const title = layer.getAttribute('data-title');
                let source;

                if (!uid && !layerUrl && !layerType) {
                    throw new Error('Layer should have uid, type and url!');
                }

                switch (layerType) {
                    case 'kml':
                        source = new VectorSource({
                            url: layerUrl,
                            format: new KML(),
                        });
                        break;
                    case 'geojson':
                        source = new VectorSource({
                            format: new GeoJSON(),
                            url: layerUrl,
                        });
                        break;
                    default:
                        source = null;
                }

                if (source) {
                    source.on('change', (e) => {
                        const target = e.target;
                        if (target.getState() === 'ready') {
                            const features = target.getFeatures();
                            features.forEach((feature) => {
                                feature.set('nom', title);
                            });
                        }
                    });

                    const mapLayer = new VectorLayer({
                        source,
                    });

                    mapLayer.set('uid', uid);
                    layers.push(mapLayer);
                }
            });
        }

        return layers;
    }

    /**
     * Transform GeoJSON to layer.
     * @param {JSON|Object} json - GeoJSON data.
     * @constructor
     */
    static GeoJSONtoLayers(json) {
        const layers = [];

        for (const layer in json) {
            if (layer) {
                const uid = +json[layer].uid;

                if (!uid) {
                    throw new Error('Layer should have uid!');
                }

                const source = new VectorSource({
                    features: (new GeoJSON()).readFeatures(json[layer], { featureProjection: 'EPSG:3857' }),
                });

                const mapLayer = new VectorLayer({
                    source,
                });

                mapLayer.set('uid', uid);
                layers.push(mapLayer);
            }
        }

        return layers;
    }

    /**
     * Create tooltip (overlay) message
     * @param {Feature} feature - instance of ol Feature.
     * @returns {string} - html markup.
     * @private
     */
    static createMessage(feature) {
        let message = '';

        if (feature.get('marker')) {
            const innerMessage = feature.get('message');

            if (innerMessage) {
                message += `<div class="popup-label__message-description">${innerMessage}</div>`;
            }
        } else {
            const title = feature.get('nom') || feature.get('title') || feature.get('name');
            const theme = feature.get('thematique');
            // const description = feature.get('description');

            if (theme) {
                message += `<p class="popup-label__message-theme">${theme}</p>`;
            }

            if (title) {
                message += `<h3 class="popup-label__message-title">${title}</h3>`;
            }
            //
            // if (description) {
            //     message += `<div class="popup-label__message-description">${description}</div>`;
            // }
        }

        return `<div class="popup-label__message-feature -${feature.getGeometry().getType().toLowerCase()}">${message}</div>`;
    }
}

export default StratisMap;
